import { Button, Card, CardContent, CardHeader, CardTitle } from '@akknerds/ui';
import { ShoppingBag, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CartLineItem } from '../components/cart/cart-line-item';
import { CartSummary } from '../components/cart/cart-summary';
import { DocumentMeta } from '../components/common/document-meta';
import { EmptyState } from '../components/common/empty-state';
import { PageHeader } from '../components/common/page-header';
import { PRELAUNCH, usePurchaseLocked } from '../config/launch';
import { useAbandonCheckout } from '../hooks/use-abandon-checkout';
import { useReconcileCart } from '../hooks/use-reconcile-cart';
import { useCartStore } from '../store/cart';

export function CartPage() {
  const items = useCartStore((s) => s.items);
  const clear = useCartStore((s) => s.clear);
  const prelaunch = usePurchaseLocked();
  const abandonStatus = useAbandonCheckout();
  const reconcileStatus = useReconcileCart({ enabled: abandonStatus === 'done' });

  if (items.length === 0) {
    return (
      <div className="container py-16">
        <DocumentMeta title="Cart" robots="noindex,nofollow" />
        <EmptyState
          icon={<ShoppingBag />}
          title="Your cart is empty"
          description="Once you add products they'll show up here."
          action={
            <Button asChild>
              <Link to="/shop">Browse products</Link>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="container py-8">
      <PageHeader title="Your cart" seoOptions={{ robots: 'noindex,nofollow' }} />
      <div className="grid items-start gap-8 lg:grid-cols-[1fr_22rem]">
        <div className="flex flex-col gap-4">
          <div className="divide-border border-border divide-y rounded-xl border px-5">
            {items.map((item) => (
              <CartLineItem key={item.product.id} item={item} />
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={clear}
            className="text-muted-foreground self-start"
          >
            <Trash2 /> Clear cart
          </Button>
        </div>

        <Card className="sticky top-20">
          <CardHeader>
            <CardTitle>Order summary</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {reconcileStatus === 'loading' && (
              <p className="text-muted-foreground text-xs">Updating prices and stock…</p>
            )}
            <CartSummary items={items} />
            {prelaunch ? (
              <Button block size="lg" disabled>
                {PRELAUNCH.buttonLabel}
              </Button>
            ) : reconcileStatus === 'loading' ? (
              <Button block size="lg" disabled>
                Updating cart…
              </Button>
            ) : (
              <Button asChild block size="lg">
                <Link to="/checkout">Proceed to checkout</Link>
              </Button>
            )}
            <Button asChild variant="outline" block>
              <Link to="/shop">Continue buying</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
